import { Controller } from '@hotwired/stimulus';
import { say } from '../lib/text';

// Copy-to-clipboard, one copy for every host (fire/docs/HOTWIRE.md).
//
// Two ways to say what to copy, because the hosts asked for both: point at
// an element that holds it,
//
//   <div data-controller="clipboard">
//     <code data-clipboard-target="content">pix-key</code>
//     <button data-action="clipboard#copy">copy</button>
//
// or hand it over directly, when the text is not on the page as text:
//
//   <button data-controller="clipboard" data-clipboard-text-value="<%= key %>"
//           data-action="clipboard#copy">copy</button>
//
// Feedback is a daisyUI tooltip on whatever was clicked — no floating node
// to position, nothing to clean up if the page navigates mid-timeout. With a
// `label` target the label's words swap for the beat instead (a button that
// says "Copy" says "Copied").
// A button that already wears a tooltip (the ident marks: hover is the
// address) gets that tip back after the flash; stripping it was the bug.
//
// No clipboard API (plain http on the LAN) or a refusal: the old road,
// execCommand over a throwaway selection.
export default class extends Controller {
  static targets = ['content', 'label'];
  static values = {
    text: String,
    successMessage: String, // the view may say it its own way; else fire-text
    errorMessage: String,
    successDuration: { type: Number, default: 2000 },
  };

  async copy(event) {
    event.preventDefault();

    const button = event.currentTarget;
    if (await this.write(this.text)) return this.flash(button, this.successMessageValue || say('copied'), 'tooltip-success');

    // Nothing worked. Selecting the text at least leaves the user one
    // keystroke away.
    if (this.hasContentTarget) this.contentTarget.select?.();
    this.flash(button, this.errorMessageValue || say('error'), 'tooltip-error');
  }

  async write(text) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      const area = document.createElement('textarea');
      area.value = text;
      area.style.cssText = 'position:fixed;opacity:0';
      document.body.appendChild(area);
      area.select();
      const ok = document.execCommand('copy');
      area.remove();
      return ok;
    }
  }

  get text() {
    if (this.hasTextValue) return this.textValue;
    if (!this.hasContentTarget) return '';
    return this.contentTarget.value ?? this.contentTarget.textContent;
  }

  flash(button, message, variant) {
    clearTimeout(this.timeout);

    if (this.hasLabelTarget) {
      const label = this.labelTarget;
      this.idle ??= label.textContent;
      label.textContent = message;
      this.timeout = setTimeout(() => (label.textContent = this.idle), this.successDurationValue);
      return;
    }
    if (!button) return;

    const priorTip = button.getAttribute('data-tip');
    const hadTooltip = button.classList.contains('tooltip');

    button.classList.add('tooltip', variant, 'tooltip-open');
    button.setAttribute('data-tip', message);

    this.timeout = setTimeout(() => {
      button.classList.remove(variant, 'tooltip-open');
      if (!hadTooltip) button.classList.remove('tooltip');
      if (priorTip && priorTip !== message) button.setAttribute('data-tip', priorTip);
      else button.removeAttribute('data-tip');
    }, this.successDurationValue);
  }

  disconnect() {
    clearTimeout(this.timeout);
  }
}
